import { findGroupByWarehouseId, getGroupAvailability, getWarehouseAvailability, groupKey, parseGroupKey, PlaceWarehouses } from '../utils'
import { selectPlaceData } from 'containers/MapDetails/placeSlice'
import useSelectedWarehouse from './useSelectedWarehouse'
import usePlaceMapParams from './usePlaceMapParams'
import type { WarehouseGroup } from '../Summary'
import { useEffect, useState } from 'react'
import { useReduxSelector } from 'store'

export interface PlaceSelection {
  /** id konkretnego magazynu (brak w trybie grup) */
  id?: string
  number?: string
  hash: string
  warehouseTypeId: string
  group: WarehouseGroup
  rentableType: warehouse.RentableType
  monthlyGrossPrice: string | number
  isAvailableNow: boolean
  availableFrom?: string
  isAvailable: boolean
  size?: {
    length?: number | string
    width?: number | string
    height?: number | string
  }
}

/** Zwraca wartość od razu, a wyczyszczenie opóźnia (na czas animacji chowania sidebara) */
const useDelayedClear = <T,>(value: T | undefined, delay = 300) => {
  const [delayed, setDelayed] = useState(value)
  useEffect(() => {
    if (value !== undefined) {
      setDelayed(value)
    } else {
      const timeout = setTimeout(() => setDelayed(undefined), delay)
      return () => clearTimeout(timeout)
    }
  }, [value, delay])
  return value !== undefined ? value : delayed
}

/** Tryb listy bez konkretnych magazynów - wybiera się grupę (wariant), a nie magazyn */
export const isGroupListMode = (place: any, isMap: boolean) => !isMap && !place?.displayOptions?.listAllWarehouses

const usePlaceSelection = (isMap: boolean, selectedGroupKey?: string) => {
  const place = useReduxSelector(selectPlaceData)
  const playgroundWarehouse = useSelectedWarehouse()
  const { id } = usePlaceMapParams()

  const isGroupMode = isGroupListMode(place, isMap)
  const warehouses = place?.warehouses as PlaceWarehouses | undefined
  // W trybie grup domyślnie zaznaczona jest grupa magazynu wybranego przez filtry (id z URL)
  const groupOfSelectedWarehouse = (() => {
    const found = isGroupMode && id ? findGroupByWarehouseId(warehouses, id) : undefined
    return found ? groupKey(found.warehouseTypeId, found.hash) : undefined
  })()
  const target = isGroupMode ? (selectedGroupKey || groupOfSelectedWarehouse) : id
  const delayedTarget = useDelayedClear(target)

  const selection = ((): PlaceSelection | undefined => {
    if (!delayedTarget || !warehouses) return undefined

    if (isGroupMode) {
      const parsed = parseGroupKey(delayedTarget)
      const group = parsed && warehouses[parsed.warehouseTypeId]?.[parsed.hash]
      if (!parsed || !group) return undefined
      return {
        ...parsed,
        group,
        rentableType: group.rentableType,
        monthlyGrossPrice: group.data?.monthlyGrossPrice,
        ...getGroupAvailability(group),
      }
    }

    const pw = playgroundWarehouse?.typeId === delayedTarget ? playgroundWarehouse : undefined
    const found = findGroupByWarehouseId(warehouses, delayedTarget)
    const warehouseTypeId = pw?.warehouseData?.attributes?.warehouseTypeId || found?.warehouseTypeId
    const hash = pw?.warehouseData?.hash || found?.hash
    const group = (warehouseTypeId && hash && warehouses[warehouseTypeId]?.[hash]) || found?.group
    if (!warehouseTypeId || !hash || !group) return undefined

    const availability = pw
      ? (() => {
        const isAvailable = !!pw.warehouseData?.status?.isAvailableForRent
        const forRent = group.for_rent?.[delayedTarget]
        return {
          isAvailable,
          isAvailableNow: isAvailable && typeof forRent !== 'string',
          availableFrom: isAvailable && typeof forRent === 'string' ? forRent : undefined,
        }
      })()
      : getWarehouseAvailability(group, delayedTarget)

    return {
      id: delayedTarget,
      number: pw?.name || group.warehousesInfo?.[delayedTarget]?.number,
      hash,
      warehouseTypeId,
      group,
      rentableType: pw?.warehouseData?.rentableType || group.rentableType,
      monthlyGrossPrice: pw?.warehouseData?.attributes?.monthlyGrossPrice || group.data?.monthlyGrossPrice,
      size: pw?.warehouseData?.size as PlaceSelection['size'],
      ...availability,
    }
  })()

  return {
    isGroupMode,
    selectedKey: target,
    isVisible: !!target,
    selection,
  }
}

export default usePlaceSelection
